import { useState, useEffect, useRef } from "react";
import { loadUsername, saveUsername, clearUsername } from "../../lib/storage";

const API_BASE = import.meta.env.VITE_API_URL;

type LeaderEntry = {
  rank: number;
  username: string;
  xp: number;
  lessonsCompleted: number;
  streak: number;
};

const TIER_ACCENT: Record<string, string> = {
  starter: "#f97316",
  builder: "#10b981",
  master:  "#6366f1",
};

export default function LeaderboardScreen({
  xp, streak, lessonsCompleted, tier, onBack,
}: {
  xp: number;
  streak: number;
  lessonsCompleted: number;
  tier: string;
  onBack: () => void;
}) {
  const accent  = TIER_ACCENT[tier] ?? "#f97316";
  const dark    = tier === "master";
  const bg      = dark ? "#0f172a" : "#fff7ed";
  const cardBg  = dark ? "#1e293b" : "#fff";
  const textCol = dark ? "#f1f5f9" : "#1f2937";
  const subCol  = dark ? "#94a3b8" : "#6b7280";
  const borderC = dark ? "#334155" : "#fef3c7";

  const [username,  setUsername]  = useState<string | null>(() => loadUsername());
  const [nameInput, setNameInput] = useState("");
  const [entries,   setEntries]   = useState<LeaderEntry[]>([]);
  const [loading,   setLoading]   = useState(false);
  const [unavail,   setUnavail]   = useState(false);
  const [editing,   setEditing]   = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  async function fetchLeaderboard(name: string) {
    setLoading(true);
    setUnavail(false);
    try {
      await fetch(`${API_BASE}/api/leaderboard`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: name, xp, lessonsCompleted, streak }),
      });
      const res = await fetch(`${API_BASE}/api/leaderboard?limit=50`);
      if (!res.ok) throw new Error();
      const data = await res.json();
      setEntries(Array.isArray(data.entries) ? data.entries : []);
    } catch {
      setUnavail(true);
    }
    setLoading(false);
  }

  useEffect(() => {
    if (username) fetchLeaderboard(username);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function sanitize(s: string) {
    return s.replace(/[^a-zA-Z0-9 _\-!.]/g, "").trim().slice(0, 20);
  }

  function handleSetName() {
    const clean = sanitize(nameInput);
    if (!clean) return;
    saveUsername(clean);
    setUsername(clean);
    setEditing(false);
    fetchLeaderboard(clean);
  }

  // ── Name entry ───────────────────────────────────────────────────────────────
  if (!username || editing) {
    return (
      <div style={{ fontFamily: "Nunito", padding: 20, paddingBottom: 100, minHeight: "100vh", background: bg }}>
        <button
          onClick={() => { setEditing(false); if (!username) onBack(); }}
          style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: textCol, marginBottom: 12, padding: "8px 0", minWidth: 44, minHeight: 44 }}
        >
          ←
        </button>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ fontSize: 60, marginBottom: 8 }}>🏅</div>
          <div style={{ fontSize: 26, fontWeight: 900, fontFamily: "Fredoka One", color: accent }}>
            {editing ? "Change Your Name" : "Join the Leaderboard!"}
          </div>
          <div style={{ fontSize: 14, color: subCol, marginTop: 6, lineHeight: 1.4 }}>
            Pick a display name — this is how you'll appear on the global rankings.
          </div>
        </div>
        <div style={{ background: cardBg, borderRadius: 22, padding: 22, boxShadow: "0 4px 20px #0002", border: `2px solid ${borderC}` }}>
          <input
            ref={inputRef}
            value={nameInput}
            onChange={e => setNameInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleSetName()}
            placeholder="Your display name"
            maxLength={20}
            autoFocus
            style={{
              width: "100%", boxSizing: "border-box", padding: "14px 16px",
              borderRadius: 14, border: `2px solid ${accent}55`, fontSize: 16,
              fontFamily: "Nunito", fontWeight: 700, outline: "none",
              background: dark ? "#0f172a" : "#fafafa", color: textCol,
            }}
          />
          <div style={{ fontSize: 11, color: subCol, marginTop: 6, textAlign: "right" }}>
            {nameInput.length}/20 · letters, numbers, spaces allowed
          </div>
          <button
            onClick={handleSetName}
            style={{
              marginTop: 14, width: "100%", padding: 16,
              background: `linear-gradient(135deg, ${accent}, ${accent}cc)`,
              borderRadius: 16, border: "none", cursor: "pointer",
              fontSize: 16, fontWeight: 900, color: "#fff", fontFamily: "Fredoka One",
              minHeight: 52, boxShadow: `0 4px 16px ${accent}44`,
            }}
          >
            {editing ? "Save Name" : "Join Leaderboard 🏅"}
          </button>
          {editing && (
            <button
              onClick={() => { clearUsername(); setUsername(null); setEditing(false); setEntries([]); }}
              style={{
                marginTop: 10, width: "100%", padding: 12,
                background: "none", border: `1.5px solid ${subCol}44`,
                borderRadius: 12, cursor: "pointer", fontSize: 13,
                color: subCol, fontFamily: "Nunito", fontWeight: 700,
              }}
            >
              Remove from leaderboard
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── Main leaderboard view ────────────────────────────────────────────────────
  const myEntry = entries.find(e => e.username === username);
  const top3    = entries.slice(0, 3);
  const rest    = entries.slice(3);

  return (
    <div style={{ fontFamily: "Nunito", padding: 16, paddingBottom: 100, minHeight: "100vh", background: bg }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
        <button
          onClick={onBack}
          style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: textCol, padding: "8px 4px", minWidth: 44, minHeight: 44 }}
        >
          ←
        </button>
        <div style={{ fontSize: 22, fontWeight: 900, fontFamily: "Fredoka One", color: accent, flex: 1 }}>
          🏅 Leaderboard
        </div>
        <button
          onClick={() => { setNameInput(username); setEditing(true); }}
          style={{ fontSize: 11, fontWeight: 800, color: accent, background: `${accent}18`, border: "none", borderRadius: 10, padding: "6px 12px", cursor: "pointer", minHeight: 32 }}
        >
          Edit name
        </button>
      </div>

      {/* My stats hero */}
      <div style={{
        background: `linear-gradient(135deg, ${accent}ee, ${accent}99)`,
        borderRadius: 22, padding: "16px 18px", marginBottom: 16,
        boxShadow: `0 8px 24px ${accent}44`, position: "relative", overflow: "hidden",
      }}>
        <div style={{ position: "absolute", top: -10, right: -10, fontSize: 80, opacity: 0.12 }}>🏅</div>
        <div style={{ fontSize: 11, fontWeight: 800, color: "#ffffffbb", marginBottom: 4, textTransform: "uppercase" as const, letterSpacing: 1 }}>
          Your Stats
        </div>
        <div style={{ fontSize: 18, fontWeight: 900, color: "#fff", fontFamily: "Fredoka One", marginBottom: 10 }}>
          {username}
          {myEntry && <span style={{ fontSize: 13, marginLeft: 8, opacity: 0.85 }}>· #{myEntry.rank} globally</span>}
        </div>
        <div style={{ display: "flex", gap: 20 }}>
          {[
            { label: "⭐ XP",       value: xp.toLocaleString()   },
            { label: "📚 Lessons",  value: String(lessonsCompleted) },
            { label: "🔥 Streak",   value: `${streak}d`           },
          ].map(s => (
            <div key={s.label} style={{ textAlign: "center" }}>
              <div style={{ fontSize: 20, fontWeight: 900, color: "#fff" }}>{s.value}</div>
              <div style={{ fontSize: 10, color: "#ffffffbb", fontWeight: 700 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div style={{ textAlign: "center", padding: 48 }}>
          <div style={{ fontSize: 40, animation: "bounce 1s infinite" }}>⏳</div>
          <div style={{ fontSize: 14, color: subCol, fontWeight: 700, marginTop: 10 }}>Loading rankings…</div>
        </div>
      )}

      {/* Unavailable */}
      {!loading && unavail && (
        <div style={{ background: cardBg, borderRadius: 20, padding: 28, textAlign: "center", border: `2px solid ${borderC}` }}>
          <div style={{ fontSize: 48 }}>🚀</div>
          <div style={{ fontSize: 18, fontWeight: 900, color: textCol, fontFamily: "Fredoka One", marginTop: 10 }}>
            Global Rankings Coming Soon!
          </div>
          <div style={{ fontSize: 13, color: subCol, marginTop: 8, lineHeight: 1.5 }}>
            Your stats are saved. Once the backend goes live your rank will appear here — keep earning XP!
          </div>
          <button
            onClick={() => fetchLeaderboard(username)}
            style={{
              marginTop: 18, padding: "12px 28px",
              background: `linear-gradient(135deg, ${accent}, ${accent}cc)`,
              borderRadius: 14, border: "none", cursor: "pointer",
              fontSize: 14, fontWeight: 900, color: "#fff", fontFamily: "Fredoka One",
            }}
          >
            Try again
          </button>
        </div>
      )}

      {/* Podium — top 3 */}
      {!loading && !unavail && top3.length === 3 && (
        <div style={{ display: "flex", alignItems: "flex-end", gap: 6, marginBottom: 16, height: 150 }}>
          {/* order: silver | gold | bronze */}
          {[top3[1], top3[0], top3[2]].map((e, pos) => {
            const heights   = [100, 140, 80];
            const podiumBgs = [
              "linear-gradient(180deg,#cbd5e1,#94a3b8)",
              "linear-gradient(180deg,#fde68a,#fbbf24)",
              "linear-gradient(180deg,#fed7aa,#f97316)",
            ];
            const medals  = pos === 0 ? "🥈" : pos === 1 ? "🥇" : "🥉";
            const isMe    = e.username === username;
            return (
              <div key={e.rank} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end" }}>
                <div style={{ fontSize: 10, fontWeight: 800, color: isMe ? accent : subCol, marginBottom: 2, textAlign: "center", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", width: "100%" }}>
                  {e.username}
                </div>
                <div style={{ fontSize: 11, fontWeight: 900, color: isMe ? accent : textCol, marginBottom: 4 }}>
                  {e.xp.toLocaleString()} XP
                </div>
                <div style={{
                  height: heights[pos], width: "100%",
                  background: podiumBgs[pos],
                  borderRadius: "10px 10px 0 0",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  boxShadow: isMe ? `0 0 0 3px ${accent}, 0 4px 12px ${accent}44` : "0 2px 8px #0002",
                }}>
                  <div style={{ fontSize: pos === 1 ? 30 : 24 }}>{medals}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Ranked list */}
      {!loading && !unavail && entries.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {entries.map(e => {
            const isMe  = e.username === username;
            const medal = e.rank === 1 ? "🥇" : e.rank === 2 ? "🥈" : e.rank === 3 ? "🥉" : null;
            return (
              <div key={`${e.rank}-${e.username}`} style={{
                background: isMe ? `${accent}18` : cardBg,
                border: `2px solid ${isMe ? accent : borderC}`,
                borderRadius: 16, padding: "12px 14px",
                display: "flex", alignItems: "center", gap: 12,
                boxShadow: isMe ? `0 4px 16px ${accent}22` : "0 2px 6px #0001",
              }}>
                <div style={{ minWidth: 36, textAlign: "center" }}>
                  {medal
                    ? <span style={{ fontSize: 22 }}>{medal}</span>
                    : <span style={{ fontSize: 14, fontWeight: 900, color: subCol }}>#{e.rank}</span>
                  }
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 900, color: isMe ? accent : textCol, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {e.username}{isMe ? " 👈 You" : ""}
                  </div>
                  <div style={{ fontSize: 11, color: subCol, fontWeight: 700 }}>
                    {e.lessonsCompleted} lessons · {e.streak}🔥
                  </div>
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <div style={{ fontSize: 16, fontWeight: 900, color: isMe ? accent : textCol }}>
                    {e.xp.toLocaleString()}
                  </div>
                  <div style={{ fontSize: 10, color: subCol, fontWeight: 700 }}>⭐ XP</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Empty state */}
      {!loading && !unavail && entries.length === 0 && (
        <div style={{ background: cardBg, borderRadius: 20, padding: 28, textAlign: "center", border: `2px solid ${borderC}` }}>
          <div style={{ fontSize: 48 }}>🏁</div>
          <div style={{ fontSize: 18, fontWeight: 900, color: textCol, fontFamily: "Fredoka One", marginTop: 10 }}>
            You're first! 🎉
          </div>
          <div style={{ fontSize: 13, color: subCol, marginTop: 8 }}>
            No one else has joined yet — keep earning XP to hold the top spot!
          </div>
        </div>
      )}
    </div>
  );
}
